import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { calendarAPI } from '../api/client';
import {
  CalendarDaysIcon, ArrowPathIcon, MapPinIcon, UserGroupIcon,
  PhoneArrowUpRightIcon,
  BellIcon, BellSlashIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const REMINDER_OPTIONS = [5, 10, 15, 30, 45, 60, 120];

// Display timezone for all dates and times.
const TZ = 'Asia/Colombo';

// Return a YYYY-MM-DD string for a Date object in TZ (used for day grouping).
function datekeyInTZ(date) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(date);
}

// Reminder-call status → badge styling + label.
const STATUS_BADGE = {
  pending: null, // shown via the reminder control instead
  sent:    { cls: 'bg-green-100 text-green-700', label: '📞 Reminder called' },
  failed:  { cls: 'bg-red-100 text-red-700',     label: '⚠ Reminder failed' },
  skipped: { cls: 'bg-slate-100 text-slate-500', label: 'Reminder missed' },
};

// Group events under "Today", "Tomorrow", or a weekday/date label in TZ.
function dateGroupLabel(d) {
  const todayKey    = datekeyInTZ(new Date());
  const tomorrowKey = datekeyInTZ(new Date(Date.now() + 86_400_000));
  const dKey        = datekeyInTZ(d);
  if (dKey === todayKey)    return 'Today';
  if (dKey === tomorrowKey) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'long', month: 'short', day: 'numeric' });
}

export default function CalendarDashboard() {
  const [conn, setConn] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [busy, setBusy] = useState({});       // per-event in-flight flag

  const load = useCallback(async () => {
    try {
      const [connRes, evRes] = await Promise.all([
        calendarAPI.getConnection(),
        calendarAPI.listEvents(),
      ]);
      setConn(connRes.data);
      setEvents(evRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await calendarAPI.sync();
      toast.success(res.data.message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const patchEvent = (id, patch) =>
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const toggleReminder = async (ev) => {
    setBusy((b) => ({ ...b, [ev.id]: true }));
    try {
      const res = await calendarAPI.updateEventReminder(ev.id, { reminder_enabled: !ev.reminder_enabled });
      patchEvent(ev.id, res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update reminder');
    } finally {
      setBusy((b) => ({ ...b, [ev.id]: false }));
    }
  };

  const changeMinutes = async (ev, value) => {
    const minutes = value === 'default' ? null : parseInt(value);
    setBusy((b) => ({ ...b, [ev.id]: true }));
    try {
      const res = await calendarAPI.updateEventReminder(ev.id, { reminder_minutes: minutes });
      patchEvent(ev.id, res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update reminder time');
    } finally {
      setBusy((b) => ({ ...b, [ev.id]: false }));
    }
  };

  const testCall = async (ev) => {
    setBusy((b) => ({ ...b, [ev.id]: true }));
    try {
      const res = await calendarAPI.testReminder(ev.id);
      toast.success(res.data.message || 'Reminder call placed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not place reminder call');
    } finally {
      setBusy((b) => ({ ...b, [ev.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Group events by calendar day in TZ (Asia/Colombo).
  const groups = [];
  let currentKey = null;
  for (const ev of events) {
    if (!ev.start_time) continue;
    const d = new Date(ev.start_time);
    const key = datekeyInTZ(d);
    if (key !== currentKey) {
      groups.push({ key, label: dateGroupLabel(d), items: [] });
      currentKey = key;
    }
    groups[groups.length - 1].items.push(ev);
  }

  // Time display: the backend is the single source of truth.
  // start_display / end_display are pre-formatted by the backend in
  // settings.timezone (Asia/Colombo) at sync time.  We show them as-is —
  // no new Date(), no toLocaleTimeString(), zero frontend timezone conversion.
  // If either field is null the row predates the current sync schema;
  // clicking "Sync Now" will populate it.
  const fmtTime = (ev) => {
    if (ev.is_all_day) return 'All day';
    if (!ev.start_display) return '';
    return ev.end_display ? `${ev.start_display} – ${ev.end_display}` : ev.start_display;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
          >
            <CalendarDaysIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Calendar</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {conn?.is_connected
                ? `${conn.google_email || 'Connected'} · reminders ${conn.reminders_enabled ? 'on' : 'off'}`
                : 'Not connected'}
            </p>
          </div>
        </div>
        {conn?.is_connected && (
          <button
            onClick={syncNow}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Now'}
          </button>
        )}
      </div>

      {/* Not connected */}
      {!conn?.is_connected ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400">
          <p className="text-4xl mb-3">📅</p>
          <p className="font-medium text-slate-600">Google Calendar isn't connected</p>
          <p className="text-sm mt-1">An administrator can connect it in Admin → Integrations → Calendar.</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400">
          <p className="text-4xl mb-3">🗓️</p>
          <p className="font-medium text-slate-600">No upcoming events</p>
          <p className="text-sm mt-1">Events from the next 30 days will appear here after a sync.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <div key={group.key}>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
                {group.label}
              </h2>
              <div className="space-y-3">
                {group.items.map((ev) => {
                  const badge = STATUS_BADGE[ev.reminder_call_status];
                  const remindersOn = conn?.reminders_enabled;
                  return (
                    <div key={ev.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-card">
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: details */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-xs font-medium text-primary-600 mb-1">
                            <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            {fmtTime(ev)}
                          </div>
                          <p className="font-semibold text-slate-800 truncate flex items-center gap-2">
                            {ev.html_link ? (
                              <a href={ev.html_link} target="_blank" rel="noreferrer" className="hover:underline truncate">
                                {ev.title}
                              </a>
                            ) : (ev.title)}
                          </p>
                          {ev.location && (
                            <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1 truncate">
                              <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" /> {ev.location}
                            </p>
                          )}
                          {ev.attendees?.length > 0 && (
                            <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1 truncate">
                              <UserGroupIcon className="w-3.5 h-3.5 flex-shrink-0" />
                              {ev.attendees.slice(0, 3).join(', ')}
                              {ev.attendees.length > 3 && ` +${ev.attendees.length - 3}`}
                            </p>
                          )}
                          {badge && (
                            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                          )}
                          {ev.reminder_call_status === 'failed' && ev.reminder_error && (
                            <p className="text-xs text-red-400 mt-1 truncate" title={ev.reminder_error}>
                              {ev.reminder_error}
                            </p>
                          )}
                        </div>

                        {/* Right: reminder controls */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <button
                            onClick={() => toggleReminder(ev)}
                            disabled={busy[ev.id]}
                            title={ev.reminder_enabled ? 'Reminder on — click to disable' : 'Reminder off — click to enable'}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
                              ev.reminder_enabled
                                ? 'text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100'
                                : 'text-slate-400 border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            {ev.reminder_enabled ? <BellIcon className="w-3.5 h-3.5" /> : <BellSlashIcon className="w-3.5 h-3.5" />}
                            {ev.reminder_enabled ? 'On' : 'Off'}
                          </button>

                          {ev.reminder_enabled && (
                            <select
                              value={ev.reminder_minutes == null ? 'default' : String(ev.reminder_minutes)}
                              onChange={(e) => changeMinutes(ev, e.target.value)}
                              disabled={busy[ev.id]}
                              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                            >
                              <option value="default">
                                Default ({conn?.default_reminder_minutes}m)
                              </option>
                              {REMINDER_OPTIONS.map((m) => (
                                <option key={m} value={m}>{m} min before</option>
                              ))}
                            </select>
                          )}

                          <button
                            onClick={() => testCall(ev)}
                            disabled={busy[ev.id] || !conn?.phone_number}
                            title={conn?.phone_number ? 'Place a reminder call now' : 'Set a phone number in Settings first'}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                          >
                            <PhoneArrowUpRightIcon className="w-3.5 h-3.5" /> Test call
                          </button>
                        </div>
                      </div>

                      {!remindersOn && ev.reminder_enabled && (
                        <p className="text-xs text-amber-500 mt-2">
                          Global reminders are off — enable them in Settings for this to fire automatically.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
